<template>
  <div>
    <div
      id="details-order-order-button"
      @mouseover="displayTooltip('details-order-order-button')"
      @mouseleave="hideTooltip('details-order-order-button')"
    >
      <ButtonComboNext 
        button-color="green"
        :active="isButtonValid"
        :clicked="checkOrderFormData"
        :price="total"
        :cents="true"
        text="Übertragen"
        text-position="left"
      />

      <Tooltip
        :delayed="true"
        :open="tooltips['details-order-order-button'].show"
        orient="top"
        :position="refPos('details-order-order-button', 'top')"
      >
        <span>
          Übertragen in die
          <br>
          EXPInet-Buchungsmaske
        </span>
      </Tooltip>
    </div>

    <div
      id="details-order-order-button-fast"
      @mouseover="displayTooltip('details-order-order-button-fast')"
      @mouseleave="hideTooltip('details-order-order-button-fast')"
    >
      <OrderButtonFast
        v-if="!!agencyId && !fastBookingExcludeAgencyList.includes(agencyId)"
        :active="isButtonValid && !cabin?.isAccessible"
      />
      
      <Tooltip
        :delayed="true"
        :open="!!priceModel && tooltips['details-order-order-button-fast'].show"
        orient="top"
        :position="refPos('details-order-order-button-fast', 'top')"
      >
        <span v-if="!!cabin && cabin.isAccessible">
          Barrierearme Kabinen sind <br>nur telefonisch unter<br>
          +49 381 20 27 07 07 buchbar.
        </span>
        <span v-else>
          Übertragen in die
          <br>Buchungsstrecke
        </span>
      </Tooltip>
    </div>

    <PdfButton
      v-if="!!agencyId && !onlyFastBookAgenciesList.includes(agencyId)"
      :active="isButtonValid"
    />

    <LinkButton
      v-if="!!agencyId && linkAgencyList.includes(agencyId)"
      :active="isButtonValid"
    />

    <Modal
      :show="showErrorModal"
      :close="hideErrorModal"
      size="small"
      oktext="zur Buchungsmaske"
      :ok="submitToOrderForm"
      canceltext="zurück zur Reisesuche"
      :cancel="hideErrorModal"
    >
      <div class="text-center fw-bold fs-16 mb-3">
        Übertragung fehlgeschlagen
      </div>
      <div class="text-center lh-base">
        Deine Reisedaten konnten nicht oder nur teilweise übernommen werden.
        Wir arbeiten bereits an einer Lösung.
        <br>
        <br>
        Du kannst zur Reisesuche zurückkehren oder ohne Auswahl fortfahren.
      </div>
    </Modal>
  </div>
</template>

<script lang="ts">
import ButtonComboNext from '@/components/common/ButtonComboNext.vue'
import { isForcedError, toolTipPosition } from '@/general/helper'
import { useCabinStore, useCruiseStore, useDetailsStore, usePriceStore, useUiStore, useUserStore } from '@/store'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import { handleError, handleInfo, handleWarning } from '../../../general/errorHandling'
import Modal from '../../common/Modal.vue'
import Tooltip from '../../common/Tooltip.vue'
import LinkButton from './LinkButton.vue'
import OrderButtonFast from './OrderButtonFast.vue'
import PdfButton from './PdfButton.vue'

export default defineComponent({
  components: {
    ButtonComboNext,
    LinkButton,
    OrderButtonFast,
    PdfButton,
    Tooltip,
    Modal
  },

  data: () => {
    return {
      orderTooltip: false,
      linkAgencyList: [
        'AIDA AKC',
        'AIDASEE',
        '40272',
        '27681',
        '39193',
        '39194',
        '39195',
        '39196',
        '41710',
        '41711',
      ],
      onlyFastBookAgenciesList: ['39190', '39193', '39194', '39195', '39196', '41710', '41711'],
      fastBookingExcludeAgencyList: ['AIDA AKC', 'AIDASEE'],
      showErrorModal: false
    }
  },

  computed: {
    ...mapState(useUiStore, {
      tooltips: store => store.tooltips,
    }),
    ...mapState(useUserStore, {
      agencyId: store => store.agencyId,
    }),
    ...mapState(useDetailsStore, {
      priceModel: store => store.priceModel,
      failedServiceForBooking: store => store.failedServiceForBooking,
    }),
    ...mapState(usePriceStore, {
      total: store => store.total,
      orderFormData: store => store.orderFormData,
      orderFormUrl: store => store.orderFormUrl,
    }),
    ...mapState(useCruiseStore, {
      cruise: store => store.cruise
    }),
    ...mapState(useCabinStore, {
      cabin: store => store.cabin,
    }),

    isButtonValid() {
      return !!this.priceModel && !!this.cruise
    },
  },

  methods: {
    ...mapActions(useUiStore, {
      hideTooltip: 'hideTooltip',
      showTooltip: 'showTooltip'
    }),
    
    refPos: (refName: string, orient: string) => {
      return toolTipPosition(refName, orient)
    },

    displayTooltip(name: string) {
      this.showTooltip({ name })
    },

    writeLogs() {
      if (!this.orderFormData) {
        handleError({
          error: new Error('No orderFormData'),
          origin: 'Übertragen',
        })
      }
      else if (!this.cruise) {
        handleWarning('Cruise is null', {
          fileName: 'OrderButtonGroup.vue',
          functionName: 'submitToOrderForm',
          data: this.orderFormData,
        })
        return
      }
    },

    checkOrderFormData() {
      if (!this.orderFormData || !!this.failedServiceForBooking || !this.cruise || isForcedError()) {
        this.showErrorModal = true
        this.writeLogs()

        return
      }

      this.submitToOrderForm()
      this.hideErrorModal()
      
      handleInfo('Übertragen', { cruiseId: this.cruise!.id, total: this.total, data: this.orderFormData })
    },

    hideErrorModal() {
      this.showErrorModal = false
    },

    submitToOrderForm() {
      const form = document.createElement('form')
      form.setAttribute('target', '_blank')
      form.setAttribute('method', 'POST')
      form.setAttribute('enctype', 'application/x-www-form-urlencoded')
      form.setAttribute('action', this.orderFormUrl)

      const formDataInput = document.createElement('input')
      formDataInput.setAttribute('name', 'p')
      formDataInput.setAttribute('value', JSON.stringify(this.orderFormData))
      form.appendChild(formDataInput)
      form.style.display = 'none'
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    },
  },
})
</script>